import { useFormikContext, getIn, Field } from 'formik';
import PropTypes from 'prop-types';
import { useMemo, useCallback } from 'react';
import { useTheme } from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { NumberInput } from '@hivebrite/design-system/Inputs';
import { Label } from '@hivebrite/design-system/Label';

import { validateNumber } from '../Number/validate';

const NumberRange = ({
  disabled,
  id,
  label,
  labelMultiline,
  maxName,
  minName,
  placeholder,
  tooltip,
  validate = {},
  wrapperProps,
}) => {
  const { required, minimum, maximum } = validate;
  const theme = useTheme();
  const { errors, values, touched } = useFormikContext();
  const htmlId = id || minName;
  const minValue = getIn(values, minName);
  const maxValue = getIn(values, maxName);
  const maxTouched = getIn(touched, maxName);
  const minError = getIn(errors, minName);
  const maxError = getIn(errors, maxName);
  const minIsHigherThanMax = minValue > maxValue;
  const minId = `${htmlId}-min`;
  const maxId = `${htmlId}-max`;

  const handleMinValidate = useCallback(
    (value) =>
      validateNumber({
        value,
        required,
        minimum,
        maximum: maxValue ?? maximum,
        maximumErrorLabel:
          minIsHigherThanMax &&
          __("The minimum can't be higher than the maximum"),
      }),
    [maximum, maxValue, minimum, minIsHigherThanMax, required],
  );
  const handleMaxValidate = useCallback(
    (value) =>
      validateNumber({
        value,
        required,
        minimum,
        maximum,
      }),
    [required, minimum, maximum],
  );
  const displayMinError = useMemo(() => {
    // Always display minimum error
    if (
      (minimum === 0 || minimum) &&
      (minValue === 0 || minValue) &&
      minValue < minimum
    ) {
      return true;
    }
    // Display minIsHigherThanMax error only if mthe max input is touched
    if (minIsHigherThanMax && maxTouched) {
      return true;
    }

    return false;
  }, [minimum, minValue, minIsHigherThanMax, maxTouched]);

  return (
    <Box mb={6} display="block" {...wrapperProps}>
      <Label
        id={minId}
        disabled={disabled}
        multiline={labelMultiline}
        required={required}
        wrapperProps={{
          opacity: disabled ? theme.input.disabled.opacity : 1,
        }}
      >
        {label}
      </Label>
      <Box>
        <Box flex={1} mr={3}>
          <Field name={minName} validate={handleMinValidate}>
            {({
              field, // { name, value, onChange, onBlur }
              meta, // { error, touched, value }
            }) => (
              <NumberInput
                {...field}
                clearable
                dataTestId="formik-range-number-min"
                disabled={disabled}
                error={meta.touched && meta.error}
                hideError
                id={minId}
                initialValue={meta.initialValue}
                inputHtml={{ ariaInvalid: minError ?? undefined }}
                label={null}
                placeholder={placeholder?.min}
                required={required}
                tooltip={tooltip}
                touched={meta.touched}
              />
            )}
          </Field>
        </Box>
        <Box flex={1} ml={3}>
          <Field name={maxName} validate={handleMaxValidate}>
            {({
              field, // { name, value, onChange, onBlur }
              meta, // { error, touched, value }
            }) => (
              <NumberInput
                {...field}
                ariaLabel={__('Max value for %{label}').i__({ label })}
                clearable
                dataTestId="formik-range-number-max"
                disabled={disabled}
                error={meta.touched && meta.error}
                hideError
                id={maxId}
                initialValue={meta.initialValue}
                inputHtml={{ ariaInvalid: maxError ?? undefined }}
                label={null}
                placeholder={placeholder?.max}
                required={required}
                tooltip={tooltip}
                touched={meta.touched}
              />
            )}
          </Field>
        </Box>
      </Box>
      {displayMinError && minError && (
        <AdditionalInformation id={minId}>{minError}</AdditionalInformation>
      )}
      {!displayMinError && maxError && (
        <AdditionalInformation id={maxId}>{maxError}</AdditionalInformation>
      )}
    </Box>
  );
};

NumberRange.propTypes = {
  disabled: PropTypes.bool,
  inputHtml: PropTypes.object,
  isClearable: PropTypes.bool,
  label: PropTypes.string,
  labelHtml: PropTypes.object,
  maxName: PropTypes.string.isRequired,
  minName: PropTypes.string.isRequired,
  placeholder: PropTypes.object,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  withoutCSS: PropTypes.bool,
  wrapperHtml: PropTypes.object,
};

export default NumberRange;
